import React, { useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import _ from 'lodash';

import { Button } from '../StyledComponents';
import { designNumbers } from '../data/index';
import DesignSlider from '../components/DesignSlider';
import useWindowDimensions from '../hooks/useWindow';
import { sendIframeHeight } from '../utils/iframePostMessage';
import { ApparelContext } from '../context/FlowContext';

const DesignNumber = ({ prevStep, formData, handleChange }) => {
  const { width } = useWindowDimensions();
  const isApparel = formData.sport === 'apparel';

  const {
    removeApparelType,
    addDesignNum,
    removeDesignNum,
    activeApparelType,
    totalSelectedDesignNums,
  } = useContext(ApparelContext);

  useEffect(() => {
    setTimeout(() => {
      sendIframeHeight();
    }, 1000);
  }, [width]);

  const selectDesignNumber = number => {
    if (isApparel) {
      addDesignNum(activeApparelType?.name, number);
    } else if (formData.designNumbers.includes(number)) return;

    let newDesignNumbers;
    newDesignNumbers = [...formData.designNumbers, number];
    handleChange({ designNumbers: newDesignNumbers });
  };

  const removeDesign = number => {
    if (isApparel) {
      removeDesignNum(activeApparelType?.name, number);
    } else {
      const newDesignNumbers = formData.designNumbers.filter(n => n !== number);
      handleChange({ designNumbers: newDesignNumbers });
    }
  };

  const horizontalSliderRef = useRef(null);

  const handleScroll = direction => {
    console.log('scrolling: ', direction);
    console.log('horizontalRef: ', horizontalSliderRef.current);
    const scrollPosition = horizontalSliderRef.current.scrollLeft;
    const padding = width < 991 ? 40 : 200;
    if (direction === 'right') {
      console.log('scroll position: ', scrollPosition + width - padding);
      horizontalSliderRef.current.scrollTo({
        behavior: 'smooth',
        left: scrollPosition + width - padding,
      });
    } else {
      horizontalSliderRef.current.scrollTo({
        behavior: 'smooth',
        left: scrollPosition - width - padding,
      });
    }
  };

  const removeNumberInstance = number => {
    if (isApparel) {
      removeDesignNum(activeApparelType?.name, number);
    } else {
      const indexOfFirstInstance = formData.designNumbers.indexOf(number);
      if (indexOfFirstInstance === -1) return;
      const newDesignNumbers = [...formData.designNumbers];
      newDesignNumbers.splice(indexOfFirstInstance, 1);
      handleChange({ designNumbers: newDesignNumbers });
    }
  };

  const addNumberInstance = number => {
    if (isApparel) {
      addDesignNum(activeApparelType?.name, number);
    } else if (formData.numDesigns === formData.designNumbers.length) return;
    const newDesignNumbers = [...formData.designNumbers, number];
    handleChange({ designNumbers: newDesignNumbers });
  };

  const infoLink =
    isApparel && designNumbers.apparel[activeApparelType?.name]?.infoLink;

  const jerseyObject = () => {
    if (isApparel) {
      return designNumbers.apparel[activeApparelType?.name].items;
    }
    if (formData.sport !== 'greek') {
      return designNumbers[formData?.sport][formData?.jerseyType];
    }
    const [sport, type] = formData.jerseyType.split('-');
    return designNumbers.greek[sport][type];
  };

  const selectedDesigns = isApparel
    ? activeApparelType?.designNums
    : formData.designNumbers;

  const handlePrevStep = () => {
    if (isApparel) {
      removeApparelType(activeApparelType?.name);
    }
    prevStep();
  };

  return (
    <StyledFormStep
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.25 }}
    >
      {isApparel ? (
        <h2>Custom {_.startCase(activeApparelType?.name)}</h2>
      ) : (
        <h2>Select jersey design</h2>
      )}
      <p>
        (Choose up to <b>{isApparel ? 'four' : formData.numDesigns}</b>{' '}
        options)
      </p>
      {formData.sport !== 'apparel' && (
        <small>
          <sup>**</sup> You can select designs from multiple categories{' '}
          <sup>**</sup>
        </small>
      )}
      <div className="row">
        {(formData.sport !== 'apparel' ||
          !activeApparelType?.designNums?.length) && (
          <button className="back-btn" onClick={handlePrevStep}>
            Back to {isApparel ? 'Apparel Catalog' : 'jersey type'}
          </button>
        )}
        {infoLink && (
          <a
            href={infoLink}
            className="back-btn"
            target="_blank"
            rel="noreferrer"
          >
            More info on our {_.startCase(activeApparelType?.name)}
          </a>
        )}
      </div>

      <div className="horizontal-slide-container" ref={horizontalSliderRef}>
        {jerseyObject().map(design => (
          <div key={design.number} className="design-container">
            <DesignSlider
              key={design.number}
              handleClick={() => selectDesignNumber(design.number)}
              sport={formData.sport}
              jerseyType={activeApparelType?.name || formData.jerseyType}
              design={design}
              isMobile={width < 991}
              disabled={
                (isApparel && totalSelectedDesignNums === 4) ||
                (formData.sport !== 'apparel' &&
                  formData?.numDesigns === selectedDesigns?.length &&
                  !selectedDesigns?.includes(design.number))
              }
            />
            <motion.div
              className="counter"
              initial={{ height: 0 }}
              animate={{
                height: selectedDesigns?.includes(design.number) ? 25 : 0,
              }}
            >
              <button onClick={() => removeNumberInstance(design.number)}>
                -
              </button>
              <p>{selectedDesigns.filter(x => x === design.number).length}</p>
              <button
                onClick={() => addNumberInstance(design.number)}
                disabled={
                  (isApparel && totalSelectedDesignNums === 4) ||
                  (formData.sport !== 'apparel' &&
                    formData?.numDesigns === selectedDesigns?.length)
                }
              >
                +
              </button>
            </motion.div>
            <div className="button-row">
              <Button
                onClick={() => selectDesignNumber(design.number)}
                active={selectedDesigns?.includes(design.number)}
                initial={{
                  padding: '.75rem 2rem',
                }}
                animate={{
                  padding: selectedDesigns?.includes(design.number)
                    ? '.75rem'
                    : '.75rem 2rem',
                }}
                style={{
                  opacity:
                    formData?.numDesigns === selectedDesigns?.length &&
                    !selectedDesigns?.includes(design.number)
                      ? 0.5
                      : 1,
                }}
                disabled={
                  (isApparel && totalSelectedDesignNums === 4) ||
                  (formData.sport !== 'apparel' &&
                    formData?.numDesigns === selectedDesigns?.length &&
                    !selectedDesigns?.includes(design.number))
                }
              >
                {selectedDesigns?.includes(design.number) ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    version="1.1"
                    x="0px"
                    y="0px"
                    viewBox="0 0 100 100"
                    style={{ enableBackground: 'new 0 0 100 100' }}
                    xmlSpace="preserve"
                  >
                    <polygon points="89.71,28.73 80.79,19.82 38.25,62.36 19.21,43.32 10.29,52.23 38.25,80.18 " />
                  </svg>
                ) : (
                  'Select'
                )}
              </Button>
              {selectedDesigns?.includes(design.number) && (
                <Button
                  onClick={() => removeDesign(design.number)}
                  active={false}
                  style={{ padding: '.75rem' }}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        ))}

        <button
          className="move-btn prev-options-btn"
          onClick={() => handleScroll('left')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            x="0px"
            y="0px"
            viewBox="0 0 100 100"
            enableBackground="new 0 0 100 100"
            xmlSpace="preserve"
          >
            <path d="M50,25.406L7.908,5v29.972v9.652v29.969L50,95l42.092-20.408V44.624v-9.652V5L50,25.406z M17.003,19.517L50,35.514  l32.997-15.997v19.408L50,54.922L17.003,38.925V19.517z M82.997,68.895L50,84.893L17.003,68.895V49.489L50,65.487l32.997-15.998  V68.895z" />
          </svg>
        </button>

        <button
          className="move-btn more-options-btn"
          onClick={() => handleScroll('right')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            x="0px"
            y="0px"
            viewBox="0 0 100 100"
            enableBackground="new 0 0 100 100"
            xmlSpace="preserve"
          >
            <path d="M50,25.406L7.908,5v29.972v9.652v29.969L50,95l42.092-20.408V44.624v-9.652V5L50,25.406z M17.003,19.517L50,35.514  l32.997-15.997v19.408L50,54.922L17.003,38.925V19.517z M82.997,68.895L50,84.893L17.003,68.895V49.489L50,65.487l32.997-15.998  V68.895z" />
          </svg>
        </button>
      </div>
    </StyledFormStep>
  );
};

const StyledFormStep = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 50px 20px 100px;

  @media (max-width: 991px) {
    padding: 20px 20px 100px;
  }

  h2 {
    margin-top: 0;
    margin-bottom: 20px !important;
    text-transform: uppercase;
    font-weight: bold;
    text-align: center;
  }

  .row {
    display: flex;
    flex-direction: row;
    gap: 20px;

    @media (max-width: 767px) {
      flex-direction: column;
    }
  }

  .back-btn {
    background-color: var(--red);
    color: #fff;
    border-radius: 4px;
    padding: 4px 8px;
    border: none;
    cursor: pointer;
    text-decoration: none;
  }

  p,
  small {
    margin-top: 0;
    margin-bottom: 10px;
    font-style: italic;
  }

  .horizontal-slide-container {
    display: flex;
    overflow-x: scroll;
    flex-direction: column;
    flex-wrap: wrap;
    width: 100%;
    max-height: 800px;
    gap: 40px;
    align-self: flex-start;
    padding: 20px 20px 80px;
    position: relative;
    scroll-snap-type: x mandatory;
  }

  .design-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    gap: 20px;

    button svg {
      width: 15px;
      fill: #fff;
    }

    .counter {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 5px;
      overflow: hidden;
      position: absolute;
      bottom: 60px;
      background-color: #fff;

      p {
        margin: 0;
        font-style: normal;
        font-weight: bold;
      }

      button {
        background-color: #dedede;
        border: none;
        height: 100%;
        cursor: pointer;
        transition: background-color 0.15s ease-in-out;

        &:hover {
          background-color: #ccc;
        }
      }
    }

    @media (max-width: 500px) {
      width: 80vw;
      scroll-snap-align: center;
    }
  }

  .button-row {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  .move-btn {
    position: fixed;
    top: 325px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
    background: linear-gradient(to right, rgba(150, 150, 150, 0), var(--red));
    padding-left: 30px;
    font-size: 40px;
    border: none;
    cursor: pointer;
    transition: all 0.25s ease-in-out;

    &.more-options-btn {
      right: 0;
    }

    &.prev-options-btn {
      left: 0;
      transform: rotate(180deg);
    }

    svg {
      width: 50px;
      transform: rotate(270deg);
      fill: #fff;
    }

    &:hover {
      background: linear-gradient(to right, rgba(250, 0, 0, 0.3), var(--red));
    }

    @media (max-width: 767px) {
      padding-left: 10px;

      svg {
        width: 30px;
      }
    }
  }
`;

export default DesignNumber;
